import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Zap, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Signed in as ${res.user.name}`);
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen bg-background grain-overlay flex flex-col">
      <div className="flex items-center justify-between p-4 md:p-6">
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <ThemeToggle />
      </div>

      <div className="flex-1 grid place-items-center px-4 pb-16">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-2.5 mb-8 justify-center">
            <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center shadow-lg shadow-primary/30">
              <Zap className="h-5 w-5 text-primary-foreground" fill="currentColor" />
            </div>
            <span className="font-heading font-extrabold text-2xl tracking-tight">WoltPe</span>
          </div>

          <Card className="p-7">
            <h1 className="font-heading text-2xl font-bold tracking-tight">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-1">Access your payment operations panel.</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email address</Label>
                <Input id="email" name="email" type="text" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com" required data-testid="login-email-input" autoComplete="username" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" required data-testid="login-password-input" autoComplete="current-password" className="pr-10" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)}
                    data-testid="toggle-password-visibility"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          </Card>

          <div className="mt-5 rounded-xl border bg-card/40 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Demo accounts</p>
            <div className="grid gap-2">
              {[
                ["Super Admin", "navneet.sogani@gmail.com", "Woltpe@2026"],
                ["Ops Admin", "admin@woltpe.com", "Admin@2026"],
                ["Distributor", "distributor@woltpe.com", "Dist@2026"],
                ["Merchant", "merchant@woltpe.com", "Merch@2026"],
              ].map(([role, em, pw]) => (
                <button key={em} onClick={() => quick(em, pw)} type="button"
                  data-testid={`demo-${role.toLowerCase().replace(" ", "-")}`}
                  className="flex items-center justify-between rounded-lg border bg-background/60 px-3 py-2 text-left text-xs hover:border-primary/40 transition-colors">
                  <span className="font-medium">{role}</span>
                  <span className="font-mono text-muted-foreground">{em}</span>
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Tap to autofill, then press Sign in.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
